import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { isNullOrUndefined } from "../../HWLibrary/HWUtils";
import { s360ConfigService } from "../../Services/S360Config";
import AudioMessage from "./AudioMessage";
import CloseMessage from "./CloseMessage";
import CustomMessage from "./CustomMessage";
import DocumentMessage from "./DocumentMessage";
import EmojiMessage from "./EmojiMessage";
import ImageMessage from "./ImageMessage";
import LocationMessage from "./LocationMessage";
import { TextMessageV1, TextMessage } from "./TextMessage";
import { SmartToyOutlined } from "@mui/icons-material";

import NCXButton from "./NCXComponents/NCXButton";
import ThreeDotLoading from "../LoaderPreview";
import NCXDropDown from "./NCXComponents/NCXDropDown";
import NCXInputText from "./NCXComponents/NCXInputText";
import NCXFileUpload from "./NCXComponents/NCXFileUpload";
import NCXRating from "./NCXComponents/NCXRating";
import NCXCarousel from "./NCXComponents/NCXCarousel";
import NCXMediaView from "./NCXComponents/NCXMediaView";
import "./NCXComponents/NCXComponents.styles.css";
import NCXCalander from "./NCXComponents/NCXCalander";
import VideoMessage from "./VideoMessage";
import NCXFileSentMessage from "./NCXComponents/NCXFileSentMessage";
import CTNButton from "./NCXComponents/CTNButton";
import CTNWelcomeMessage from "./NCXComponents/CTNWelcomeMessage";
import { NCXFAQ } from "./NCXComponents/NCXFAQ";
import flowMemorySingleton from "../../Services/FlowMemorySingleton";
import NCXTranscriptFileView from "./NCXComponents/NCXTranscriptFileView";
import apiService from "../../Services/APIService";
import { debounce } from "../utils/ConversationUtils";
import { convertCreatedAtToUserTime } from "../../../utils/dateConversion";

const UserIcon = React.memo(
  (
    { creator }, // Memoize UserIcon
  ) => (
    <div
      className="user-icon fade-in"
      style={{
        width: "30px",
        height: "30px",
        borderRadius: "50%",
        backgroundColor: creator === "USER" ? "#4caf50" : "#015feb",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: "#fff",
        marginRight: creator !== "USER" ? "10px" : "0",
        marginLeft: creator === "USER" ? "10px" : "0",
      }}
    >
      <SmartToyOutlined style={{ color: "white" }} />
    </div>
  ),
);

function firstMessageClosureFunction() {
  function setFirstMessageDone(id) {
    s360ConfigService.firstMessageDone = id;
  }
  function firstMessageIsDone() {
    return s360ConfigService && s360ConfigService.firstMessageDone;
  }
  function setFirstButtonDone(id) {
    s360ConfigService.firstButtonDone = id;
  }
  function firstButtonIsDone() {
    return s360ConfigService && s360ConfigService.firstButtonDone;
  }

  return {
    setFirstMessageDone,
    firstMessageIsDone,
    setFirstButtonDone,
    firstButtonIsDone,
  };
}
const {
  firstMessageIsDone,
  setFirstMessageDone,
  setFirstButtonDone,
  firstButtonIsDone,
} = firstMessageClosureFunction();

const debouncedAttachmentOpen = debounce(
  (lastMessageIndex, messageID, setEnableAttachmentProp) => {
    if (!flowMemorySingleton.getLastShownMessageIdForAutoAttachment()) {
      if (lastMessageIndex === messageID) {
        flowMemorySingleton.setLastShownMessageIdForAutoAttachment(messageID);
        setEnableAttachmentProp(messageID);
      }
    }
  },
  500,
);

const Message = React.memo(
  function MessageComponent(props) {
    const {
      sendMessageToPreview,
      isLatestMessage,

      lastMessageIndex,
      currentMessageIndex,
      messageID,
      setEnableAttachmentProp,
      setFileParamsProp,
      id,
      downloadInProgress,
      setInputSpecsProp,
      message,
      hideInputForHisry,
      workitemId,
      isEdited,
      isDeleted,
      fromHistory = false,
      ticketId = "",
      sessionId,
    } = props;
    const [loading, setLoading] = useState(true);
    const [buttonsDisabled, setButtonsDisabled] = useState(false);
    const [messageStatus, setMessageStatus] = useState("SENT");

    const loaderTimeoutRef = useRef(null);

    const creator = useMemo(() => {
      if (props.message.messageMediaType === "CUSTOM") {
        return props.message?.userType;
      }
      const ut = props.message?.userType;
      return ut === "AGENT" || ut === "SYSTEM" || ut === "BOT" ? ut : "USER";
    }, [props.message]);

    useEffect(() => {
      return () => {
        if (loaderTimeoutRef.current) {
          clearTimeout(loaderTimeoutRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (creator === "AGENT") {
        s360ConfigService.agentConnected = true;
        // Force show input box when agent connects
        props.setShowInputBox(true);
        s360ConfigService.config.showInputBox = true;
      }
    }, [creator, props.setShowInputBox]);

    useEffect(() => {
      if (creator === "USER" || creator === "CUSTOMER") {
        let latestStatus = "SENT";
        if (message.messageStatus && Array.isArray(message.messageStatus)) {
          // First check for READ status
          const readStatus = message.messageStatus.find(
            (status) => status.status === "READ",
          );
          if (readStatus) {
            latestStatus = "READ";
          } else {
            const deliveredStatus = message.messageStatus.find(
              (status) => status.status === "DELIVERED",
            );
            if (deliveredStatus) {
              latestStatus = "DELIVERED";
            }
          }
        }
        setMessageStatus(latestStatus);
      } else {
        // Don't show ticks on BOT or AGENT message
        setMessageStatus(null);
      }
    }, [props.message, creator]);

    const setLoaderWithTimeout = useCallback(
      (loaderConfig, timeoutMs = 5000) => {
        if (loaderTimeoutRef.current) {
          clearTimeout(loaderTimeoutRef.current);
        }

        props.setInputSpecsProp(loaderConfig);

        loaderTimeoutRef.current = setTimeout(() => {
          props.setInputSpecsProp({});
          loaderTimeoutRef.current = null;
        }, timeoutMs);
      },
      [props.setInputSpecsProp],
    );

    // Effects moved below after dependencies are initialized

    const clearLoaderAndTimeout = useCallback(() => {
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
        loaderTimeoutRef.current = null;
      }
      props.setInputSpecsProp({});
    }, [props.setInputSpecsProp]);

    const senderName = useCallback(() => {
      if (message?.userType === "AGENT") {
        const message = props?.message;
        const fromUser = message?.fromUser;
        const showAgentInfo =
          s360ConfigService?.config?.configDetails?.showAgentInfo;
        const inValidNames = ["null null", "null"];

        if (showAgentInfo) {
          console.log(fromUser);
          const nickNameFromConfig = fromUser.nickName
            ? fromUser.nickName
            : fromUser.name;
          if (
            nickNameFromConfig &&
            !inValidNames.includes(nickNameFromConfig.trim().toLowerCase())
          )
            return nickNameFromConfig;

          // if (
          //   fromUser?.nickName &&
          //   !inValidNames.includes(fromUser?.nickName.trim().toLowerCase())
          // )
          // return fromUser?.nickName;
          // if (
          //   fromUser?.name &&
          //   !inValidNames.includes(fromUser?.name.trim().toLowerCase())
          // )
          //   return fromUser?.name;

          return "AGENT";
        } else {
          return "AGENT";
        }
      }
    }, [props?.message]);

    // Effects to avoid updating parent during render, placed after declarations

    const parsedMessage = useMemo(() => {
      // useMemo for data transformations
      return props.message.messageMediaType === "CUSTOM"
        ? props.message.message
          ? JSON.parse(props.message.message)
          : null
        : {
            creator: "USER",
            components: [
              {
                subType: "default",
                text: `${props.message.message}`,
                category: "display",
                type: "text",
                richText: "",
              },
              {
                category: "input",
                type: "text",
                validation: {},
              },
            ],
          };
    }, [props.message]);

    const components = useMemo(
      () => parsedMessage?.components || [],
      [parsedMessage],
    );

    // Effects to avoid updating parent during render
    useEffect(() => {
      if (s360ConfigService.agentConnected) {
        props.setShowInputBox(true);
        s360ConfigService.config.showInputBox = true;
        return;
      }

      if (props.isAiBot || props.message.messageSubType === "AIEMPLOYEE") {
        props.setShowInputBox(true);
        s360ConfigService.config.showInputBox = true;
      } else {
        if (!flowMemorySingleton.getAgentMessageStarted()) {
          props.setShowInputBox(false);
          s360ConfigService.config.showInputBox = false;
        }
      }
    }, [props.isAiBot, creator]);

    useEffect(() => {
      const hideTypes = new Set([
        "button",
        "carousel",
        "calendar",
        "list",
        "rating",
      ]);
      const hasHide = components.some(
        (c) => c?.category === "input" && hideTypes.has(c?.type),
      );
      if (typeof props.showTextBox === "function") {
        props.showTextBox(!hasHide);
      }
    }, [components, props.showTextBox]);

    useEffect(() => {
      const fileComp = components.find(
        (c) => c?.category === "input" && c?.type === "file",
      );
      if (fileComp) {
        setFileParamsProp(fileComp?.validation);
        flowMemorySingleton.setIsFileParamsPresent(true);
        if (!props.isAiBot) {
          props.setShowInputBox(true);
        }
        debouncedAttachmentOpen(
          lastMessageIndex,
          messageID,
          setEnableAttachmentProp,
        );
      } else {
        if (flowMemorySingleton.getIsFileParamsPresent()) {
          setFileParamsProp({});
          flowMemorySingleton.setIsFileParamsPresent(false);
        }
      }
    }, [
      components,
      lastMessageIndex,
      messageID,
      setEnableAttachmentProp,
      props.isAiBot,
      setFileParamsProp,
    ]);

    useEffect(() => {
      const textInputComp = components.find(
        (c) => c?.category === "input" && c?.type === "text",
      );
      if (!textInputComp) {
        props.setShowInputBox(false);
        s360ConfigService.config.showInputBox = false;
        return;
      }
      props.setShowInputBox(true);
      s360ConfigService.config.showInputBox = true;
      if (textInputComp?.placeholderText) {
        // Update only the placeholder; no loader here

        clearLoaderAndTimeout();
        props.setInputSpecsProp({
          placeholderText: textInputComp?.placeholderText,
        });
      } else {
        // Ensure no lingering loader due to text inputs
        clearLoaderAndTimeout();
      }
    }, [
      components,
      props.message?.userType,
      clearLoaderAndTimeout,
      props.setInputSpecsProp,
    ]);

    // Previously invoked during render; move to effects
    useEffect(() => {
      props.setInputSpecsProp({});
    }, [messageID]);

    useEffect(() => {
      if (
        loading &&
        messageID === lastMessageIndex &&
        !message.hasOwnProperty("convoType")
      ) {
        // Show loader only in intended cases
        if (props.isAiBot && creator === "USER") {
          setLoaderWithTimeout({ showLoadingFrom: "Thinking..." }, 10000);
        } else if (!props.isAiBot && creator === "BOT") {
          setLoaderWithTimeout({ showLoadingFrom: "BOT" }, 5000);
        } else {
          props.setInputSpecsProp({});
        }
      }
    }, [
      loading,
      messageID,
      lastMessageIndex,
      message,
      creator,
      props.isAiBot,
      props.setInputSpecsProp,
    ]);

    useEffect(() => {
      if (creator === "USER") {
        setButtonsDisabled(true);
        flowMemorySingleton.setAgentMessageStarted(true);
      }
      if (creator === "BOT") {
        flowMemorySingleton.setAgentMessageStarted(false);
      }
    }, [creator]);

    if (creator === "AGENT" && !flowMemorySingleton.getAgentMessageStarted()) {
      flowMemorySingleton.setAgentMessageStarted(true);
    }

    const fetchPreviewFromApiForBotAttachments = (mData) => {
      let stringUrl = "";
      if (mData?.mediaPath) stringUrl = mData?.mediaPath;
      if (mData?.mediaUrl) stringUrl = mData?.mediaUrl;

      let previewUrl = stringUrl.replace("/download/storage/v1/b/", "/");
      previewUrl = previewUrl.replace("/o/", "/");

      return previewUrl;
    };

    const fetchPreviewFromApiForAgentAttachments = (workitemId, title) => {
      const url = apiService.getFile(workitemId, title);
      return url;
    };

    const getComponent = useCallback(
      // useCallback for component creation
      (component, index) => {
        const {
          type,
          subType,
          category,
          text,
          data,
          validation,
          placeholderText,
          richText,
        } = component;

        window.parent.postMessage(
          {
            function: "preview-node-outline",
            nodeId: parsedMessage.nodeId,
          },
          "*",
        );

        // file params cleanup handled via effect

        let content;

        switch (category) {
          case "display":
            switch (type) {
              case "text":
                if (
                  // Render CTNWelcomeMessage only once:
                  // - Only for very first message in the conversation
                  // - Only for the first display/text component of that message
                  currentMessageIndex === 0 &&
                  index === 0 &&
                  (!firstMessageIsDone() ||
                    JSON.stringify(firstMessageIsDone()) ===
                      JSON.stringify(id)) &&
                  s360ConfigService &&
                  s360ConfigService.config &&
                  s360ConfigService.config.connectToNextiva
                ) {
                  setFirstMessageDone(id);
                  content = (
                    <CTNWelcomeMessage
                      richText={richText}
                      text={text}
                      key={`${id}-ctnwelcomemessage`}
                      id={id}
                    />
                  );
                } else {
                  const attachments = props.allAttachments;
                  if (attachments && attachments.length > 0) {
                    content = (
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems:
                            props.message.userType === "CUSTOMER" ||
                            props.message.userType === "USER"
                              ? "flex-end"
                              : "flex-start",
                        }}
                      >
                        {text !== undefined &&
                        text !== null &&
                        text !== "null" &&
                        text !== "" &&
                        text !== "undefined" ? (
                          <TextMessage
                            sandBoxedAgentInfo={props.sandBoxedAgentInfo}
                            sandBoxedReadreciept={props.sandBoxedReadreciept}
                            handleDeleteMessage={props.handleDeleteMessage}
                            onEditMessage={props.onEditMessage}
                            messageStatus={messageStatus}
                            messageId={messageID}
                            senderName={senderName}
                            richText={richText}
                            text={text}
                            creator={creator}
                            lastMessageIndex={lastMessageIndex}
                            currentMessageIndex={currentMessageIndex}
                            key={`${id}-textmessage`}
                            senderId={props.message?.fromUser?.userId}
                            receivedTimestamp={props.message?.createdAt}
                            workitemId={workitemId}
                            message={props.message.message}
                            isEdited={isEdited}
                            isDeleted={isDeleted}
                            downloadInProgress={downloadInProgress}
                            messageSubType={props.message?.messageSubType}
                          />
                        ) : null}
                        {downloadInProgress ? (
                          attachments?.map((attachment, aIdx) => (
                            <NCXTranscriptFileView
                              key={`${id}-att-${aIdx}-${
                                attachment?.title || "file"
                              }`}
                              fileName={
                                creator !== "BOT"
                                  ? attachment.title
                                  : "Bot Attachment"
                              }
                              fileUrl={
                                creator !== "BOT"
                                  ? fetchPreviewFromApiForAgentAttachments(
                                      props.workitemId,
                                      attachment.title,
                                    )
                                  : fetchPreviewFromApiForBotAttachments(
                                      attachment,
                                    )
                              }
                              id={id}
                              creator={creator}
                              senderId={props.message?.fromUser?.userId}
                              receivedTimestamp={props.message?.createdAt}
                              senderName={senderName}
                              messageId={messageID}
                            />
                          ))
                        ) : (
                          <NCXMediaView
                            {...props}
                            mediaData={attachments}
                            key={`${id}-ncxmediaview`}
                            id={id}
                            agentText
                            messageId={messageID}
                            workitemId={props.workitemId}
                            creator={creator}
                            messageStatus={messageStatus}
                            senderId={props.message?.fromUser?.userId}
                            senderName={senderName}
                            messageStatus={messageStatus}
                            receivedTimestamp={props.message?.createdAt}
                          />
                        )}
                      </div>
                    );
                  } else {
                    content = (
                      <TextMessage
                        sandBoxedAgentInfo={props.sandBoxedAgentInfo}
                        sandBoxedReadreciept={props.sandBoxedReadreciept}
                        handleDeleteMessage={props.handleDeleteMessage}
                        onEditMessage={props.onEditMessage}
                        messageStatus={messageStatus}
                        messageId={messageID}
                        senderName={senderName}
                        richText={richText}
                        text={text}
                        creator={creator}
                        lastMessageIndex={lastMessageIndex}
                        currentMessageIndex={currentMessageIndex}
                        key={`${id}-textmessage`}
                        senderId={props.message?.fromUser?.userId}
                        receivedTimestamp={props.message?.createdAt}
                        workitemId={workitemId}
                        message={props.message.message}
                        isEdited={isEdited}
                        isDeleted={isDeleted}
                        downloadInProgress={downloadInProgress}
                        messageSubType={props.message?.messageSubType}
                      />
                    );
                  }
                }
                break;
              case "media":
                content = downloadInProgress ? (
                  data?.map((item, mIdx) => (
                    <NCXTranscriptFileView
                      key={`${id}-media-${mIdx}-${item?.title || "file"}`}
                      fileName={
                        creator !== "BOT" ? item.title : "Bot Attachment"
                      }
                      fileUrl={
                        creator !== "BOT"
                          ? fetchPreviewFromApiForAgentAttachments(
                              props.workitemId,
                              item.title,
                            )
                          : fetchPreviewFromApiForBotAttachments(item)
                      }
                      id={id}
                      creator={creator}
                      senderId={props.message?.fromUser?.userId}
                      receivedTimestamp={props.message?.createdAt}
                      senderName={senderName}
                      messageId={messageID}
                    />
                  ))
                ) : (
                  <NCXMediaView
                    {...props}
                    mediaData={data}
                    key={`${id}-ncxmediaview`}
                    id={id}
                    messageId={messageID}
                    creator={creator}
                    messageStatus={messageStatus}
                    senderId={props.message?.fromUser?.userId}
                    senderName={senderName}
                    messageStatus={messageStatus}
                    receivedTimestamp={props.message?.createdAt}
                  />
                );
                break;
              case "file":
                content = downloadInProgress ? (
                  <NCXTranscriptFileView
                    fileName={component?.attachmentTitle}
                    fileUrl={component?.url}
                    id={id}
                    creator="USER"
                  />
                ) : (
                  <NCXFileSentMessage
                    {...props}
                    mediaData={component}
                    creator={creator}
                    messageStatus={messageStatus}
                    key={`${id}-ncxfilesentmessage`}
                    id={id}
                    creator={creator}
                    messageStatus={messageStatus}
                  />
                );
                break;
              case "carousel":
                content = (
                  <NCXCarousel
                    {...props}
                    data={component}
                    key={`${id}-ncxcarousel`}
                    id={id}
                  />
                );
                break;
              case "faq":
                content = (
                  <NCXFAQ
                    {...props}
                    data={component}
                    key={`${id}-ncxfaq`}
                    id={id}
                    ticketId={ticketId}
                    sessionId={sessionId}
                  />
                );
                break;
              case "Aiemployee":
                break;
              default:
                content = (
                  <TextMessage
                    sandBoxedAgentInfo={props.sandBoxedAgentInfo}
                    sandBoxedReadreciept={props.sandBoxedReadreciept}
                    {...props}
                    text="Unsupported Display Type"
                    key={`${id}-textmessage`}
                    id={id}
                    messageId={messageID}
                    senderId={props.message?.fromUser?.userId}
                    receivedTimestamp={props.message?.createdAt}
                    senderName={senderName}
                    downloadInProgress={downloadInProgress}
                    messageSubType={props.message?.messageSubType}
                  />
                );
                break;
            }
            break;

          case "input":
            switch (type) {
              case "text":
                content = null;

                break;
              case "button":
                if (
                  (!firstButtonIsDone() ||
                    JSON.stringify(firstButtonIsDone()) ===
                      JSON.stringify(id)) &&
                  s360ConfigService &&
                  s360ConfigService.config &&
                  s360ConfigService.config.connectToNextiva
                ) {
                  setFirstButtonDone(id);
                  content = (
                    <CTNButton
                      buttons={data}
                      sendMessageToPreview={sendMessageToPreview}
                      key={`${id}-ctnbutton`}
                      id={id}
                      disabled={
                        !isLatestMessage || hideInputForHisry || fromHistory
                      }
                    />
                  );
                } else {
                  content = (
                    <NCXButton
                      engagementId={id}
                      ticketId={ticketId}
                      messageId={messageID}
                      lastMessageIndex={lastMessageIndex}
                      currentMessageIndex={currentMessageIndex}
                      sendMessageToPreview={sendMessageToPreview}
                      {...props}
                      buttons={data}
                      subType={subType}
                      key={`${id}-ncxbutton-${buttonsDisabled}`}
                      id={id}
                      disabled={
                        !isLatestMessage || buttonsDisabled || fromHistory
                      }
                    />
                  );
                }
                break;
              case "carousel":
                content = (
                  <NCXCarousel
                    {...props}
                    carouselData={component}
                    key={`${id}-ncxcarousel`}
                    id={id}
                  />
                );
                break;
              case "calendar":
                content = (
                  <NCXCalander
                    {...props}
                    text={text}
                    subType={subType}
                    validation={validation}
                    sendMessageToPreview={sendMessageToPreview}
                    key={`${id}-ncxcalendar`}
                    id={id}
                    disabled={!isLatestMessage || fromHistory}
                  />
                );
                break;
              case "list":
                content = (
                  <NCXDropDown
                    sendMessageToPreview={sendMessageToPreview}
                    listData={component}
                    key={`${id}-ncxdropdown`}
                    id={id}
                    disabled={!isLatestMessage || fromHistory}
                  />
                );
                break;
              case "file":
                content = null;
                break;
              case "rating":
                content = (
                  <NCXRating
                    sendMessageToPreview={sendMessageToPreview}
                    {...props}
                    text={data?.text}
                    validation={validation}
                    subType={subType}
                    componentId={data.componentId}
                    key={`${id}-ncxrating`}
                    id={id}
                    disabled={!isLatestMessage || fromHistory}
                  />
                );
                break;
              default: {
                content = (
                  <TextMessage
                    sandBoxedAgentInfo={props.sandBoxedAgentInfo}
                    sandBoxedReadreciept={props.sandBoxedReadreciept}
                    {...props}
                    text="Unsupported Input Type"
                    key={`${id}-textmessage`}
                    messageId={messageID}
                    senderId={props.message?.fromUser?.userId}
                    receivedTimestamp={props.message?.createdAt}
                    senderName={senderName}
                    downloadInProgress={downloadInProgress}
                    messageSubType={props.message?.messageSubType}
                  />
                );
              }
            }
            break;
          default:
            content = (
              <TextMessage
                sandBoxedAgentInfo={props.sandBoxedAgentInfo}
                sandBoxedReadreciept={props.sandBoxedReadreciept}
                {...props}
                text="Unsupported Category"
                key={`${id}-textmessage`}
                messageId={messageID}
                senderId={props.message?.fromUser?.userId}
                receivedTimestamp={props.message?.createdAt}
                senderName={senderName}
                downloadInProgress={downloadInProgress}
                messageSubType={props.message?.messageSubType}
              />
            );
        }

        return (
          <div
            key={`${id}-cmp-${index}-${type}`}
            className="message-container fade-in"
            style={{
              display: "flex",
              justifyContent:
                creator === "USER" || creator === "CUSTOMER"
                  ? "flex-end"
                  : "flex-start",
              alignItems: "self-start",
              marginBottom: "0.5rem",
              transition: "all 0.5s ease",
            }}
          >
            <div
              key={`${id}-contentdivcontainer`}
              style={{
                width: type === "carousel" || type === "faq" ? "100%" : "auto",
                overflowWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              {content}
            </div>
          </div>
        );
      },
      [
        props,
        creator,
        senderName,
        downloadInProgress,
        lastMessageIndex,
        currentMessageIndex,
        messageID,
        parsedMessage,
        id,
        setFileParamsProp,
        setEnableAttachmentProp,
        isLatestMessage,
        buttonsDisabled,
        components,
        messageStatus,
      ],
    );

    useEffect(() => {
      if (creator !== "USER") {
        const timer = setTimeout(() => {
          setLoading(false);
        }, 500);
        return () => clearTimeout(timer);
      } else {
        setLoading(false);
      }
    }, [creator]);

    if (
      ["USER", "CUSTOMER"].includes(creator) &&
      !s360ConfigService?.firstUserMessageSent
    ) {
      s360ConfigService.firstUserMessageSent = true;
    }

    return (
      <>
        {downloadInProgress && props.message.userType !== "BOT" ? (
          <span
            className="messageTimeForHistory"
            style={{
              fontWeight: 800,
              textAlign: props.message.userType === "AGENT" ? "start" : "end",
              marginTop: "16px",
              marginBottom: "4px",
            }}
          >
            {props?.message?.fromUser?.name ? props.message.userType : null}
          </span>
        ) : null}
        {downloadInProgress && props.message.userType === "BOT" ? (
          <span
            className="messageTimeForHistory"
            style={{
              fontWeight: 800,
              textAlign: "start",
              marginBottom: "4px",
            }}
          >
            BOT
          </span>
        ) : null}
        {downloadInProgress ? (
          <span
            className="messageTimeForHistory"
            style={{
              textAlign: ["CUSTOMER", "USER"].includes(props.message.userType)
                ? "end"
                : "start",
              marginBottom: "4px",
            }}
          >
            {convertCreatedAtToUserTime(props.createdAt)}
          </span>
        ) : null}
        {downloadInProgress ? (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {components.map((component, index) =>
              getComponent(component, index),
            )}
          </div>
        ) : (
          components.map((component, index) => getComponent(component, index))
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    // Comparison function
    return (
      prevProps.id === nextProps.id &&
      prevProps.isLatestMessage === nextProps.isLatestMessage &&
      prevProps.messageID === nextProps.messageID &&
      prevProps.currentMessageIndex === nextProps.currentMessageIndex &&
      JSON.stringify(prevProps.message) === JSON.stringify(nextProps.message) && // Compare message objects
      JSON.stringify(prevProps.allAttachments) ===
        JSON.stringify(nextProps.allAttachments) && // Compare attachments
      prevProps.downloadInProgress === nextProps.downloadInProgress &&
      prevProps.workitemId === nextProps.workitemId &&
      prevProps.createdAt === nextProps.createdAt &&
      prevProps.isEdited === nextProps.isEdited &&
      prevProps.isDeleted === nextProps.isDeleted &&
      prevProps.hideInputForHisry === nextProps.hideInputForHisry &&
      prevProps.sandBoxedReadreciept === nextProps.sandBoxedReadreciept &&
      prevProps.sandBoxedAgentInfo === nextProps.sandBoxedAgentInfo
    );
  },
);

export const ConvoType = {
  TEXT: "TEXT",
  IMAGE: "IMAGE",
  CUSTOM: "CUSTOM",
  VIDEO: "VIDEO",
  AUDIO: "AUDIO",
  LOCATION: "LOCATION",
  FILE: "FILE",
  CLOSE: "CLOSE",
  TRANSFER: "TRANSFER",
  DOCUMENT: "DOCUMENT",
  EMOJI: "EMOJI",
};

export const MessageType = {
  received: "RECEIVED",
  sent: "SENT",
  info: "INFO",
};

export default Message;

export const MessageV1 = React.memo(
  function MessageV1(props) {
    //Memoize MessageV1 Component
    const showNewConversationOverlay =
      s360ConfigService.config.hideNewConversation;

    const getMessageComponent = useCallback(() => {
      //useCallback for component creation
      let convoType = props.message.convoType;

      switch (convoType) {
        case ConvoType.TEXT:
          return <TextMessageV1 {...props} />;
        case ConvoType.EMOJI:
          return <EmojiMessage {...props} />;
        case ConvoType.IMAGE:
          return <ImageMessage {...props} />;
        case ConvoType.CUSTOM:
          return (
            <CustomMessage
              {...props}
              end={props?.message?.message?.chatResponse?.end}
              disabled={props.message.disabled}
            />
          );
        case ConvoType.VIDEO:
          return <VideoMessage {...props} />;
        case ConvoType.AUDIO:
          return <AudioMessage {...props} />;
        case ConvoType.LOCATION:
          return <LocationMessage {...props} />;
        case ConvoType.FILE:
          return <TextMessageV1 {...props} />;
        case ConvoType.CLOSE:
          return isNullOrUndefined(showNewConversationOverlay) ||
            !showNewConversationOverlay ? (
            <CloseMessage {...props} />
          ) : null;
        case ConvoType.TRANSFER:
          return <TextMessageV1 {...props} />;
        case ConvoType.DOCUMENT:
          return <DocumentMessage {...props} />;
        default:
          return <TextMessageV1 {...props} />;
      }
    }, [props]);

    return <>{getMessageComponent()}</>;
  },
  (prevProps, nextProps) => {
    // Comparison Function
    return (
      JSON.stringify(prevProps.message) === JSON.stringify(nextProps.message) &&
      prevProps.config === nextProps.config
    );
  },
);
